import { StatusValue } from "@/types/types";
import { create } from "zustand";

export interface SearchResult {
  id: string;
  title: string;
  subtitle?: string;
  url: string;
  category?: string;
  avatar?: string;
  status?: StatusValue;
}

interface SearchState {
  // State
  query: string;
  results: SearchResult[];
  isLoading: boolean;
  isOpen: boolean;
  recentSearches: string[];

  // Actions
  setQuery: (query: string) => void;
  setResults: (results: SearchResult[]) => void;
  setIsLoading: (isLoading: boolean) => void;
  setIsOpen: (isOpen: boolean) => void;
  addRecentSearch: (query: string) => void;
  clearRecentSearches: () => void;
  performSearch: (
    query: string,
    organizationId: string,
    token: string
  ) => Promise<void>;
  resetSearch: () => void;
}

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;

const emptyResults: SearchResult[] = [];

export const useSearchStore = create<SearchState>((set, get) => ({
  // Initial state
  query: "",
  results: emptyResults,
  isLoading: false,
  isOpen: false,
  recentSearches: JSON.parse(
    localStorage.getItem("recent-searches") || "[]"
  ).slice(0, 5),

  setQuery: (query: string) => set({ query }),

  setResults: (results: SearchResult[]) => set({ results }),

  setIsLoading: (isLoading: boolean) => set({ isLoading }),

  setIsOpen: (isOpen: boolean) => {
    set({ isOpen });
    // Reset search when closing
    if (!isOpen) {
      set({ query: "", results: emptyResults });
    }
  },

  addRecentSearch: (query: string) => {
    const trimmedQuery = query.trim();
    if (!trimmedQuery) return;

    const currentSearches = get().recentSearches;
    const filtered = currentSearches.filter((s) => s !== trimmedQuery);
    const newSearches = [trimmedQuery, ...filtered].slice(0, 5);

    set({ recentSearches: newSearches });
    localStorage.setItem("recent-searches", JSON.stringify(newSearches));
  },

  clearRecentSearches: () => {
    set({ recentSearches: [] });
    localStorage.removeItem("recent-searches");
  },

  performSearch: async (
    query: string,
    organizationId: string,
    token: string
  ) => {
    const trimmedQuery = query.trim();

    // If empty query, show empty results
    if (!trimmedQuery) {
      set({ results: emptyResults, isLoading: false });
      return;
    }

    set({ isLoading: true, query: trimmedQuery });

    try {
      const response = await fetch(`${API_BASE_URL}/api/search/global`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          query: trimmedQuery,
          organization_id: organizationId,
        }),
      });

      if (!response.ok) {
        throw new Error("Failed to perform search");
      }

      const data = await response.json();

      if (data?.success) {
        set({ results: data.results, isLoading: false });
      } else {
        set({ results: emptyResults, isLoading: false });
      }
    } catch (error) {
      console.error("Error performing search:", error);
      set({ results: emptyResults, isLoading: false });
    }
  },

  resetSearch: () => {
    set({
      query: "",
      results: emptyResults,
      isLoading: false,
    });
  },
}));
